'use client'

import React, { forwardRef, useId } from "react"
import * as Headless from '@headlessui/react'
import clsx from 'clsx'
import { LayoutGroup } from 'motion/react'
import { TouchTarget } from '@/components/reusable/button'
import { Link } from 'gatsby';

export function Sidebar({ className, ...props }) {
  return <nav {...props} className={clsx(className, 'flex h-full min-h-0 flex-col')} />
}

export function SidebarHeader({ className, ...props }) {
  return (
    <div
      {...props}
      className={clsx(
        className,
        'flex flex-col border-b px-4 py-6 border-zinc-950/5 dark:border-zinc-100/10'
      )}
    />
  )
}

export function SidebarBody({ className, ...props }) {

  return (
    <div
      {...props}
      className={clsx(
        className,
        'flex flex-1 flex-col overflow-y-auto px-4 py-6'
      )}
    />
  )
}

export function SidebarFooter({ className, ...props }) {
  return (
    <div
      {...props}
      className={clsx(
        className,
        'sidebar-footer flex flex-col border-t px-4 py-6 border-zinc-950/5 dark:border-zinc-100/10'
      )}
    />
  )
}

export function SidebarSection({ className, ...props }) {
  let id = useId()

  return (
    <LayoutGroup id={id}>
      <div {...props} data-slot="section" className={clsx(className, 'flex flex-col')} />
    </LayoutGroup>
  )
}

export function SidebarSpacer({ className, ...props }) {
  return <div aria-hidden="true" {...props} className={clsx(className, 'flex-1')} />
}

/**
 * Wrapper component for external links in sidebar
 * Prevents Gatsby Link from being used with external URLs
 */
const ExternalLink = forwardRef(function ExternalLink({ to, children, className, ...props }, ref) {
  return (
    <a href={to} ref={ref} className={className} target="_blank" rel="noopener noreferrer" {...props}>
      {children}
    </a>
  );
});

export const SidebarItem = forwardRef(function SidebarItem(
  { current, className, children, onClick, to, disabled, ...props },
  ref
) {
  let classes = clsx(
    // Base
    'flex w-full items-center gap-2.5 rounded-lg px-2.5 text-left sm:py-2.5 text-zinc-950 dark:text-zinc-100',
    // Hover
    'data-hover:bg-zinc-950/5 dark:data-hover:bg-zinc-100/10',
    // Active
    current ? 'font-bold' : 'font-normal',
    // Disabled - explicitly add pointer-events-none
    disabled ? 'opacity-50 pointer-events-none' : ''
  )

  // Handle clicks - prevent navigation when disabled
  const handleClick = (e) => {
    if (disabled) {
      e.preventDefault();
      return;
    }

    if (onClick) {
      onClick(e);
    }
  };

  // Check if this is an external link
  const isExternalLink = to && (to.startsWith('http://') || to.startsWith('https://'));

  return (
    <span className={clsx(className, 'relative!')}>
      {to ? (
        <Headless.CloseButton
          as={isExternalLink ? ExternalLink : Link}
          to={disabled ? "#" : to}
          {...props}
          className={classes}
          data-current={current ? 'true' : undefined}
          data-disabled={disabled ? 'true' : undefined}
          ref={ref}
          onClick={handleClick}
        >
          <TouchTarget>{children}</TouchTarget>
        </Headless.CloseButton>
      ) : (
        <Headless.Button
          {...props}
          className={clsx('cursor-pointer', classes)}
          data-current={current ? 'true' : undefined}
          data-disabled={disabled ? 'true' : undefined}
          ref={ref}
          onClick={handleClick}
        >
          <TouchTarget>{children}</TouchTarget>
        </Headless.Button>
      )}
    </span>
  )
})

export function SidebarLabel({ className, ...props }) {
  return <span {...props} className={clsx(className, 'truncate')} />
}